import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import '../../styles/ChangePassword.css';  // src/styles/ChangePassword.css

/**
 * ChangePassword Component
 * 
 * Allows logged-in users to change their own password.
 * Uses JWT token for authentication - user ID is extracted from token.
 * 
 * Props:
 * - onPasswordChanged: Callback function after successful password change
 * - isForced: Boolean - if true, user must change password (can't close/skip)
 * - onClose: Callback to close the modal (disabled if isForced=true)
 */
const ChangePassword = ({ onPasswordChanged, isForced = false, onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    if (!formData.currentPassword) {
      setError('Please enter your current password');
      return false;
    }
    if (!formData.newPassword) {
      setError('Please enter a new password');
      return false;
    }
    if (formData.newPassword.length < 4) {
      setError('New password must be at least 4 characters');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New password and confirmation do not match');
      return false;
    }
    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth.php?action=change_password`,
        {
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword
        },
        { 
          withCredentials: true,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setSuccess('Password changed successfully!');
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        
        // Call callback after short delay
        setTimeout(() => {
          if (onPasswordChanged) {
            onPasswordChanged();
          }
        }, 1500);
      } else {
        setError(response.data.error || response.data.message || 'Failed to change password');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Error changing password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`change-password-container ${isForced ? 'forced' : ''}`}>
      <div className="change-password-card">
        <div className="change-password-header">
          <h2>🔐 Change Password</h2>
          {isForced && (
            <p className="forced-message">
              You must change your password before continuing.
            </p>
          )}
          {!isForced && onClose && (
            <button className="close-btn" onClick={onClose}>×</button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="change-password-form">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => togglePasswordVisibility('current')}
              >
                {showPasswords.current ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => togglePasswordVisibility('new')}
              >
                {showPasswords.new ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <small className="hint">Minimum 4 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-actions">
            {!isForced && onClose && (
              <button 
                type="button" 
                className="btn btn-cancel" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
            )}
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>

        <div className="password-tips">
          <h4>Password Tips:</h4>
          <ul>
            <li>Use at least 4 characters</li>
            <li>Mix letters and numbers for better security</li>
            <li>Don't reuse old passwords</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;